import type {
  ArchiveDetail,
  ArchiveScope,
  ArchiveSession,
  Phase,
  PhaseConfig,
  RevealEvalFlags,
  RevealOutputFlags,
  RevealState,
  Topic,
  RequirementFields,
  EvalEvent,
  Evaluation,
  Output,
  OutputContentType,
  Role,
  Vote,
  VoteTally,
  AuditEntry,
  Team,
  TeamRequirement,
} from "@offsite/shared";

import { config as offsiteConfig } from "@active-offsite/offsite.config";

// API-Basis pro Offsite: im Dev der Vite-Proxy (/api), in Prod die
// offsite-spezifische API-Domain aus der OffsiteConfig. (Früher global via
// VITE_API_URL — das band ALLE Offsites an die Vertrieb-API.)
const BASE_URL = import.meta.env.DEV ? "/api" : offsiteConfig.apiBaseUrl;

// Must match STORAGE_KEY in state/store.tsx — keep in sync.
const AUTH_STORAGE_KEY = "offsite:auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

let unauthorizedHandled = false;

function handleUnauthorized() {
  // Only redirect once; multiple in-flight 401s shouldn't cascade.
  if (unauthorizedHandled) return;
  unauthorizedHandled = true;
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    /* noop */
  }
  // Hard redirect — clears all in-memory state, prevents UI/store drift.
  // Don't trigger when already on the login page (avoids reload loops).
  if (window.location.pathname !== "/") {
    window.location.assign("/?session=expired");
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init;
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(headers as Record<string, string> | undefined),
    },
  });
  const text = await res.text();
  const body: unknown = text ? (tryParse(text) ?? text) : null;
  if (!res.ok) {
    // 401 → kick to login immediately so the UI doesn't stay broken.
    // The caller still receives a thrown ApiError to abort its flow.
    if (res.status === 401 && token) {
      handleUnauthorized();
    }
    throw new ApiError(res.status, body, `${rest.method ?? "GET"} ${path} → ${res.status}`);
  }
  return body as T;
}

function tryParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  auth: {
    login: (password: string) =>
      request<{ token: string; role: Role }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
    /**
     * Einmal-Code-Login (Strategietag: `codeLogin`). Getrennt von `login`,
     * weil Erfolgs- und Fehlerform verschieden sind (tier statt nur role;
     * 401 "Code unbekannt" / 409 "Dieser Code wurde schon benutzt" statt
     * generischem Passwortfehler).
     */
    loginCode: (code: string) =>
      request<{ token: string; role: Role; tier?: "buehne" | "hintergrund" }>(
        "/auth/login",
        {
          method: "POST",
          body: JSON.stringify({ code }),
        },
      ),
    /**
     * Reviewer-Magic-Link redemption. The server validates the JWT (must be
     * `role=reviewer` + `readOnly=true`, not expired) and echoes the token
     * back so the frontend can persist it like a normal login.
     */
    magic: (token: string) =>
      request<{ token: string; role: Role; readOnly: true; exp: number }>(
        `/auth/magic?token=${encodeURIComponent(token)}`,
      ),
  },
  phase: {
    get: async (token: string | null) => {
      // GET /phase liefert seit dem Strategietag-Umbau den öffentlichen
      // Vertrag (flaches JSON für die Seitenbildschirme) und das volle
      // PhaseConfig unter `intern`. Die alte Form { phase: {...} } bleibt als
      // Fallback lesbar, damit ein frisches Frontend gegen eine noch nicht
      // deployte API nicht bricht.
      const r = await request<{
        intern?: PhaseConfig;
        phase: PhaseConfig | string;
      }>("/phase", { token });
      if (r.intern) return r.intern;
      return r.phase as PhaseConfig;
    },
    set: async (
      token: string | null,
      phase: Phase,
      timerDurationSec?: number,
      opts?: {
        anzeigePhase?: PhaseConfig["anzeigePhase"];
        phaseDauerSec?: number;
        gruppeStart?: boolean;
        gruppeLabel?: string;
        /** Warten ohne neue Runde: schon Angemeldete bleiben im Zähler. */
        rundeBehalten?: boolean;
      },
    ) => {
      const r = await request<{ phase: PhaseConfig }>("/phase", {
        method: "PUT",
        token,
        body: JSON.stringify({ phase, timerDurationSec, ...opts }),
      });
      return r.phase;
    },
    /**
     * Der öffentliche /phase-Vertrag im Ganzen (Zeitstempel, Dauern, counts,
     * serverTime) — für Abschlussfolie und Timer-Anzeigen. Kein Token nötig.
     */
    kontrakt: () =>
      request<{
        phase: string;
        phaseLabel: string;
        phaseStartedAt: string | null;
        phaseDuration: number | null;
        groupStartedAt: string | null;
        groupDuration: number | null;
        groupLabel: string | null;
        counts: {
          stageSubmitted: number;
          stageTotal: number;
          backgroundSubmitted: number;
          submittedGroup: number;
          submittedToday: number;
          generatedToday: number;
        };
        serverTime: string;
        intern: PhaseConfig;
      }>("/phase"),
  },
  /**
   * Zahlen der laufenden Runde. Auth-only, nicht presenter-only: Pult und
   * Teilnehmer-Handy zeigen dieselben Zahlen.
   */
  runde: {
    get: (token: string | null) =>
      request<{ angemeldet: number; eingereicht: number; buehne?: number; publikum?: number }>("/runde", { token }),
  },
  notizen: {
    list: (token: string | null) =>
      request<{ notizen: { id: string; text: string; createdAt: number }[] }>("/notizen", {
        token,
      }),
    add: (token: string | null, text: string) =>
      request<{ notiz: { id: string; text: string; createdAt: number } }>("/notizen", {
        method: "POST",
        token,
        body: JSON.stringify({ text }),
      }),
    remove: (token: string | null, id: string) =>
      request<{ ok: boolean }>(`/notizen/${id}`, { method: "DELETE", token }),
  },
  winner: {
    /** Aktueller Sieger — für alle Eingeloggten (eingefroren schlägt Tallies). */
    get: (token: string | null) =>
      request<{ winner: { topicId: string; topicTitle: string } | null }>("/winner", {
        token,
      }),
    /** Setzt den Sieger-Topic manuell (Moderator-Auswahl bei Gleichstand). */
    set: (token: string | null, topicId: string) =>
      request<{ winningTopicId: string; winningTopicTitle: string }>("/winner", {
        method: "POST",
        token,
        body: JSON.stringify({ topicId }),
      }),
  },
  topics: {
    list: (token: string | null) => request<{ topics: Topic[] }>("/topics", { token }),
    set: (token: string | null, titles: string[]) =>
      request<{ topics: Topic[] }>("/topics", {
        method: "POST",
        token,
        body: JSON.stringify({ topics: titles }),
      }),
  },
  groups: {
    join: (
      token: string | null,
      body: { topicId: string; name: string; role: "writer" | "viewer" },
    ) =>
      request<{ ok: true; role: "writer" | "viewer" }>("/groups/join", {
        method: "POST",
        token,
        body: JSON.stringify(body),
      }),
    stats: (token: string | null) =>
      request<{ stats: { topicId: string; count: number; writerName?: string }[] }>(
        "/groups/stats",
        { token },
      ),
  },
  requirements: {
    get: async (token: string | null, topicId: string) => {
      const r = await request<{
        requirement: {
          topicId: string;
          fields: RequirementFields;
          status: "draft" | "submitted";
          submittedAt?: number;
          updatedAt: number;
        };
      }>(`/requirements/${topicId}`, { token });
      return { fields: r.requirement.fields, status: r.requirement.status };
    },
    put: (token: string | null, topicId: string, fields: RequirementFields) =>
      request<{ requirement: unknown }>(`/requirements/${topicId}`, {
        method: "PUT",
        token,
        body: JSON.stringify({ fields }),
      }).then(() => ({ ok: true as const })),
    submit: (token: string | null, topicId: string) =>
      request<{ requirement: unknown }>(`/requirements/${topicId}/submit`, {
        method: "POST",
        token,
      }).then(() => ({ ok: true as const })),
  },
  evaluations: {
    trigger: (token: string | null) =>
      request<{ ok: true }>("/evaluate", { method: "POST", token }),
    list: (token: string | null) =>
      request<{ evaluations: Evaluation[] }>("/evaluations", { token }),
    get: (token: string | null, teamNumber: number) =>
      request<{ evaluation: Evaluation }>(`/evaluations/${teamNumber}`, { token }),
  },
  overnight: {
    trigger: (token: string | null) =>
      request<{ ok: true }>("/overnight/trigger", { method: "POST", token }),
    status: (token: string | null) =>
      request<{
        status: "idle" | "running" | "complete" | "error";
        done: number;
        total: number;
        phase: string;
        /** Teamnummern, für die gebaut wird. Ältere Deployments liefern sie nicht. */
        teams?: number[];
        /** Teamnummern, die fertig sind. */
        fertig?: number[];
        /** Geschriebene Zeichen je noch laufendem Prototyp. */
        fortschritt?: Record<number, number>;
      }>("/overnight/status", { token }),
    outputs: (token: string | null) =>
      request<{ outputs: Output[] }>("/overnight/outputs", { token }),
    output: (token: string | null, teamNumber: number) =>
      request<{ output: Output }>(`/overnight/outputs/${teamNumber}`, { token }),
  },
  votes: {
    cast: (token: string | null, topicId: string) =>
      request<{ ok: true; vote: Vote }>("/vote", {
        method: "POST",
        token,
        body: JSON.stringify({ topicId }),
      }),
    me: (token: string | null) =>
      request<{ vote: Vote | null }>("/vote/me", { token }),
    tallies: (token: string | null) =>
      request<{ tallies: VoteTally[] }>("/votes", { token }),
    reset: (token: string | null) =>
      request<{ ok: true }>("/votes/reset", { method: "POST", token }),
  },
  eval: {
    progress: (token: string | null) =>
      request<{ total: number; completed: number }>("/eval/progress", { token }),
    events: (token: string | null) =>
      request<{ events: EvalEvent[] }>("/eval/events", { token }),
  },
  reveal: {
    get: (token: string | null) =>
      request<{ reveal: RevealState }>("/reveal", { token }),
    setEval: (
      token: string | null,
      teamNumber: number,
      patch: Partial<RevealEvalFlags>,
    ) =>
      request<{ eval: RevealEvalFlags }>(`/reveal/eval/${teamNumber}`, {
        method: "PUT",
        token,
        body: JSON.stringify(patch),
      }),
    setOutput: (
      token: string | null,
      teamNumber: number,
      revealed: boolean,
    ) =>
      request<{ output: RevealOutputFlags }>(`/reveal/output/${teamNumber}`, {
        method: "PUT",
        token,
        body: JSON.stringify({ revealed }),
      }),
  },
  admin: {
    addTopic: (token: string | null, title: string) =>
      request<{ topic: Topic }>("/admin/topics", {
        method: "POST",
        token,
        body: JSON.stringify({ title }),
      }),
    updateTopic: (token: string | null, topicId: string, title: string) =>
      request<{ topic: Topic }>(`/admin/topics/${topicId}`, {
        method: "PUT",
        token,
        body: JSON.stringify({ title }),
      }),
    deleteTopic: (token: string | null, topicId: string) =>
      request<{ ok: true }>(`/admin/topics/${topicId}`, {
        method: "DELETE",
        token,
      }),
    putRequirement: (
      token: string | null,
      topicId: string,
      fields: RequirementFields,
    ) =>
      request<{ requirement: unknown }>(`/admin/requirements/${topicId}`, {
        method: "PUT",
        token,
        body: JSON.stringify({ fields }),
      }),
    setTimer: (
      token: string | null,
      durationSec: number,
      mode: "set" | "extend" = "set",
    ) =>
      request<{ phase: PhaseConfig }>("/admin/timer", {
        method: "PUT",
        token,
        body: JSON.stringify({ durationSec, mode }),
      }),
    /** Phasen-Uhr der Timer-Pille: pause friert ein, weiter läuft mit der
     *  Restzeit, neustart stempelt frisch mit der übergebenen Dauer. */
    phasenUhr: (
      token: string | null,
      aktion: "pause" | "weiter" | "neustart",
      durationSec?: number,
    ) =>
      request<{ phase: PhaseConfig }>("/admin/timer", {
        method: "PUT",
        token,
        body: JSON.stringify({ mode: `phase-${aktion}`, durationSec }),
      }),
    pauseTimer: (token: string | null) =>
      request<{ phase: PhaseConfig }>("/admin/timer", {
        method: "PUT",
        token,
        body: JSON.stringify({ mode: "pause" }),
      }),
    resumeTimer: (token: string | null) =>
      request<{ phase: PhaseConfig }>("/admin/timer", {
        method: "PUT",
        token,
        body: JSON.stringify({ mode: "resume" }),
      }),
    resetTimer: (token: string | null) =>
      request<{ phase: PhaseConfig }>("/admin/timer", {
        method: "PUT",
        token,
        body: JSON.stringify({ mode: "reset" }),
      }),
    evalStatus: (token: string | null) =>
      request<{
        total: number;
        completed: number;
        perTeam: {
          teamNumber: number;
          teamName: string | null;
          status: "no_requirement" | "draft" | "submitted" | "evaluated";
          score: number | null;
          zusammenfassung: string | null;
          evaluatedAt: number | null;
          /** Ältere Deployments liefern das Feld nicht — deshalb optional. */
          createdAt?: number;
        }[];
      }>("/admin/eval-status", { token }),
    audit: (token: string | null) =>
      request<{ entries: AuditEntry[] }>("/admin/audit", { token }),
    assignTeams: (token: string | null, numTeams: number) =>
      request<{
        totalVoters: number;
        numTeams: number;
        perTeam: number[];
        phase: PhaseConfig;
      }>("/admin/teams/assign", {
        method: "POST",
        token,
        body: JSON.stringify({ numTeams }),
      }),
    teamSummary: (token: string | null) =>
      request<{
        totalVoters: number;
        totalAssigned: number;
        teams: { number: number; count: number }[];
      }>("/admin/teams", { token }),
    reset: (token: string | null, scope: ArchiveScope, label?: string) =>
      request<{ archiveId: string; itemCount: number }>("/admin/reset", {
        method: "POST",
        token,
        body: JSON.stringify({ scope, label }),
      }),
    /**
     * Gruppenwechsel: archiviert Teams, Prompts und Ergebnisse der laufenden
     * Besuchergruppe. Rahmenthema, Phase und Passwort bleiben stehen — sie
     * gehören zum Tag, nicht zur Gruppe.
     */
    /** Zurufe aus dem Publikum (Abschlussfolie): eintippen + auflisten. */
    ideeAbsenden: (token: string | null, text: string) =>
      request<{ idee: { id: string; text: string; createdAt: number } }>(
        "/admin/ideen",
        { method: "POST", token, body: JSON.stringify({ text }) },
      ),
    ideenReset: (token: string | null) =>
      request<{ ok: true }>("/admin/ideen-reset", { method: "POST", token }),
    ideenAggregat: (token: string | null) =>
      request<{ begriffe: { text: string; gewicht: number }[]; stand: number }>(
        "/admin/ideen-aggregat",
        { token },
      ),
    ideenListe: (token: string | null) =>
      request<{
        ideen: { id: string; text: string; gruppeLabel?: string; createdAt: number }[];
      }>("/admin/ideen", { token }),
    /** Alle eingereichten Anforderungen (laufende Runde + Archive) zur
     *  Nachbereitung — das Pult macht daraus eine CSV. */
    export: (token: string | null) =>
      request<{
        zeilen: {
          gruppe: string;
          archiviertAm: number | null;
          teamNumber: number;
          name: string | null;
          abteilung: string | null;
          rolle: string | null;
          status: string;
          thema: string;
          was: string;
          fuerWen: string;
          problem: string;
          anforderungen: string;
          einschraenkungen: string;
          beispiel: string;
          resultId: string | null;
        }[];
      }>("/admin/export", { token }),
    resetGroup: (token: string | null, label?: string) =>
      request<{ archiveId: string; itemCount: number }>("/admin/reset", {
        method: "POST",
        token,
        body: JSON.stringify({ scope: "gruppe" satisfies ArchiveScope, label }),
      }),
    archives: (token: string | null) =>
      request<{ archives: ArchiveSession[] }>("/admin/archives", { token }),
    archive: (token: string | null, archiveId: string) =>
      request<{ archive: ArchiveDetail }>(`/admin/archives/${archiveId}`, {
        token,
      }),
    /**
     * Notfall-Modus: vorbereitete Ergebnisse statt Live-Generierung.
     * Nur der Moderator sieht den Zustand — die Leinwand zeigt nichts davon.
     */
    fallback: (token: string | null) =>
      request<{ enabled: boolean; preparedCount: number }>("/admin/fallback", {
        token,
      }),
    setFallback: (token: string | null, enabled: boolean) =>
      request<{ enabled: boolean; preparedCount: number }>("/admin/fallback", {
        method: "PUT",
        token,
        body: JSON.stringify({ enabled }),
      }),
    /**
     * Einmal-Codes für den Einlass erzeugen (Bühne/Hintergrund). Batch —
     * siehe MAX_CODE_BATCH im Backend.
     */
    createCodes: (
      token: string | null,
      body: { buehne: number; hintergrund: number; label: string },
    ) =>
      request<{ codes: { code: string; rolle: "buehne" | "hintergrund" }[] }>(
        "/admin/codes",
        {
          method: "POST",
          token,
          body: JSON.stringify(body),
        },
      ),
    /**
     * Team-Code (netzwirtschaft): mehrfach einlösbar, feste Teamnummer —
     * ein QR-Code pro Team auf der Leinwand.
     */
    createTeamCode: (token: string | null, team: number, label?: string) =>
      request<{ codes: { code: string; teamNumber?: number }[] }>(
        "/admin/codes",
        {
          method: "POST",
          token,
          body: JSON.stringify({ team, ...(label ? { label } : {}) }),
        },
      ),
    listCodes: (token: string | null) =>
      request<{
        codes: {
          code: string;
          rolle: "buehne" | "hintergrund";
          label: string;
          createdAt: number;
          usedAt?: number;
          name?: string;
          abteilung?: string;
          teamNumber?: number;
          mehrfach?: boolean;
        }[];
      }>("/admin/codes", { token }),
  },
  /** Rahmenthema der Runde — Moderator setzt, alle lesen. */
  framing: {
    get: (token: string | null) =>
      request<{ topic: string }>("/framing", { token }),
    set: (token: string | null, topic: string) =>
      request<{ topic: string }>("/framing", {
        method: "PUT",
        token,
        body: JSON.stringify({ topic }),
      }),
  },
  teams: {
    list: (token: string | null) =>
      request<{ teams: Team[] }>("/teams", { token }),
    /** Solo-Offsites: ein Teilnehmer = ein Team. Idempotent. */
    join: (token: string | null) =>
      request<{ team: Team }>("/teams/join", { method: "POST", token }),
    get: (token: string | null, n: number) =>
      request<{ team: Team }>(`/teams/${n}`, { token }),
    setName: (token: string | null, n: number, name: string) =>
      request<{ team: Team }>(`/teams/${n}/name`, {
        method: "PUT",
        token,
        body: JSON.stringify({ name }),
      }),
    /** Feste Teams: Schriftführer-Rolle übernehmen (letzter Klick gewinnt). */
    claimCaptain: (token: string | null, n: number) =>
      request<{ team: Team }>(`/teams/${n}/captain`, {
        method: "POST",
        token,
      }),
    getReq: (token: string | null, n: number) =>
      request<{ requirement: TeamRequirement }>(`/teams/${n}/requirement`, {
        token,
      }),
    putReq: (token: string | null, n: number, fields: RequirementFields) =>
      request<{ requirement: TeamRequirement }>(`/teams/${n}/requirement`, {
        method: "PUT",
        token,
        body: JSON.stringify({ fields }),
      }),
    submitReq: (token: string | null, n: number) =>
      request<{ requirement: TeamRequirement }>(
        `/teams/${n}/requirement/submit`,
        { method: "POST", token },
      ),
    reopenReq: (token: string | null, n: number) =>
      request<{ requirement: TeamRequirement }>(
        `/teams/${n}/requirement/reopen`,
        { method: "POST", token },
      ),
    /** Pflicht-Identität nach Code-Login (siehe OffsiteConfig.identity). */
    setIdentity: (
      token: string | null,
      n: number,
      name: string,
      abteilung: string,
    ) =>
      request<{ team: Team }>(`/teams/${n}/identity`, {
        method: "PUT",
        token,
        body: JSON.stringify({ name, abteilung }),
      }),
  },
  /**
   * Dauerhafter, unauthentifizierter Abruf des eigenen Ergebnisses unter
   * `resultId` — kein `token`-Parameter, weil der Endpunkt bewusst ohne Auth
   * läuft (die resultId ist die Berechtigung, siehe API-Kontrakt).
   */
  ergebnis: {
    get: (resultId: string) =>
      request<{
        status: "pending" | "complete" | "error";
        name: string;
        thema: string;
        contentType?: OutputContentType;
        content?: string;
      }>(`/ergebnis/${encodeURIComponent(resultId)}`),
  },
};
